import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Switch,
  Table,
  Typography,
} from 'antd';
import _ from 'lodash';
import { Activity, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CustomerSearch from '../../../components/ui/CustomerSearch';
import {
  addAssignCustomer,
  addAssignUser,
  addUser,
  deleteAssignedCustomer,
  deleteAssignedUser,
  getAssignedCustomers,
  getAssignedUsers,
  getAssignUsersList,
  getUser,
  updateUser,
} from '../../../redux/actions/users';
import { getRoutes, ROLES } from '../../../routes/Routes';

const AddUser = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authSlice);
  const { customers: allCustomers } = useSelector((state) => state.customersSlice);
  const { userId } = useParams();
  const [form] = Form.useForm();

  const [usersList, setUsersList] = useState([]);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [resetPassword, setResetPassword] = useState(false);

  const type = Form.useWatch('type', form);

  const roleOptions = _.chain(ROLES)
    ?.toPairs()
    ?.map(([label, value]) => ({
      label: _.startCase(label.toLowerCase()),
      value,
    }))
    ?.filter((role) => role.value > user.role)
    ?.value();

  const handleAddUser = async () => {
    if (!selectedUser) return;
    const userObj = usersList.find((u) => u.id === selectedUser);
    if (!userObj) return;
    if (users.some((u) => u.id === userObj.id)) return;

    const res = await addAssignUser({ lead_id: userId, id: selectedUser });
    if (res?.ack) {
      setUsers((prev) => [...prev, userObj]);
      setSelectedUser(null);
    }
  };

  const handleAddCustomer = async () => {
    if (!selectedCustomer) return;
    const customerObj = allCustomers.find((c) => c.customer_netsuite_id === selectedCustomer);
    if (!customerObj) return;
    if (customers.some((c) => c.customer_netsuite_id === customerObj.customer_netsuite_id)) return;

    const res = await addAssignCustomer({
      id: userId,
      customer_netsuite_id: selectedCustomer,
    });

    if (res?.ack) {
      setCustomers((prev) => [...prev, customerObj]);
      setSelectedCustomer(null);
    }
  };

  const handleRemoveRow = async (id, type) => {
    if (type === 'user') {
      const res = await deleteAssignedUser({ id });
      if (res?.ack) setUsers((prev) => prev.filter((item) => item.id !== id));
    } else if (type === 'customer') {
      const res = await deleteAssignedCustomer({ customer_netsuite_id: id });
      if (res?.ack) setCustomers((prev) => prev.filter((item) => item.customer_netsuite_id !== id));
    }
  };

  const userColumns = [
    {
      title: 'Name',
      dataIndex: 'user_name',
      key: 'user_name',
      className: 'py-1',
    },
    { title: 'Email', dataIndex: 'email', key: 'email', className: 'py-1' },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      align: 'center',
      className: 'py-1',
      render: (_, record) => (
        <Popconfirm
          title="Remove this user?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => handleRemoveRow(record.id, 'user')}
        >
          <Button
            size="small"
            color="danger"
            variant="filled"
            className="h-auto w-auto"
            icon={<DeleteOutlined className="flex text-xs" />}
          />
        </Popconfirm>
      ),
    },
  ];

  const customerColumns = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 100, className: 'py-1' },
    { title: 'Title', dataIndex: 'title', key: 'title', className: 'py-1' },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      align: 'center',
      className: 'py-1',
      render: (_, record) => (
        <Popconfirm
          title="Remove this customer?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => handleRemoveRow(record.customer_netsuite_id, 'customer')}
        >
          <Button
            size="small"
            color="danger"
            variant="filled"
            className="h-auto w-auto"
            icon={<DeleteOutlined className="flex text-xs" />}
          />
        </Popconfirm>
      ),
    },
  ];

  const onFinish = async (values) => {
    if (userId) values.id = userId;
    if (!values?.netsuite_id) values.netsuite_id = null;

    const res = userId ? await updateUser(values) : await addUser(values);
    if (res?.ack && !userId) navigate(getRoutes().user_details.route(res?.record_id));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        setResetPassword(true);
        form.resetFields();
        return;
      }

      try {
        const [assignRes, userRes] = await Promise.all([
          getAssignUsersList(userId),
          getUser(userId),
        ]);

        if (assignRes?.ack) {
          setUsersList(assignRes.user_list || []);
        }

        if (!userRes?.ack) return;

        const { user: fetchedUser } = userRes;
        form.setFieldsValue({ ...fetchedUser, password: '' });

        const userPromise =
          fetchedUser.type === ROLES.TEAM_LEAD ? getAssignedUsers(userId) : Promise.resolve(null);

        const customerPromise =
          [ROLES.TEAM_LEAD, ROLES.SALES_REP, ROLES.CUSTOMER].includes(fetchedUser.type)
            ? getAssignedCustomers(userId)
            : Promise.resolve(null);

        const [assignedUsersRes, assignedCustomersRes] = await Promise.all([
          userPromise,
          customerPromise,
        ]);

        if (assignedUsersRes?.ack) {
          setUsers(assignedUsersRes.assigned_users || []);
        }

        if (assignedCustomersRes?.ack) {
          const assignedCustomers = assignedCustomersRes.assigned_customers || [];

          const customersById = _.keyBy(allCustomers, 'customer_netsuite_id');
          const mergedCustomers = assignedCustomers.map((assigned) => ({
            ...assigned,
            ...customersById[assigned.customer_netsuite_id],
          }));

          setCustomers(mergedCustomers);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [userId, allCustomers, form]);

  return (
    <div className="add-user-page">
      <div className="flex align-items-center justify-content-between flex-wrap gap-2">
        <Typography.Title level={3} className="page-title m-0">
          {userId ? 'Update' : 'Add'} User
        </Typography.Title>

        <Activity mode={userId ? 'visible' : 'hidden'}>
          <div className="flex align-items-center gap-2">
            <Typography.Text>Reset Password</Typography.Text>
            <Switch
              checked={resetPassword}
              onChange={(checked) => setResetPassword(checked)}
              disabled={!userId}
            />
          </div>
        </Activity>
      </div>

      <Form
        layout="vertical"
        name="editUser"
        form={form}
        onFinish={onFinish}
        size="medium"
        initialValues={{ status: 0, type: ROLES.CUSTOMER }}
      >
        <Row gutter={[8, 0]}>
          <Col sm={24} md={12} lg={8} className='w-full'>
            <Form.Item
              label="First Name"
              name="first_name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input placeholder="Jon" />
            </Form.Item>
          </Col>

          <Col sm={24} md={12} lg={8} className='w-full'>
            <Form.Item label="Last Name" name="last_name">
              <Input placeholder="Doe" />
            </Form.Item>
          </Col>

          <Col sm={24} md={12} lg={8} className='w-full'>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Invalid email format' },
              ]}
            >
              <Input placeholder="jondoe@example.com" />
            </Form.Item>
          </Col>

          <Col sm={24} md={12} lg={6} className='w-full'>
            <Form.Item label="Phone Number" name="phone">
              <Input placeholder="123-456-7890" />
            </Form.Item>
          </Col>

          <Col sm={24} md={12} lg={6} className='w-full'>
            <Form.Item
              label="Netsuite ID"
              name="netsuite_id"
              rules={[{
                required: [2, 3].includes(type)
              }]}
            >
              <Input placeholder="1234" />
            </Form.Item>
          </Col>

          <Activity mode={resetPassword ? 'visible' : 'hidden'}>
            <Col sm={24} md={12} lg={6} className='w-full'>
              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: 'Please enter password' }]}
              >
                <Input.Password placeholder="Password" />
              </Form.Item>
            </Col>
          </Activity>

          <Col sm={24} md={12} lg={6} className="w-full">
            <Form.Item
              label="Role"
              name="type"
              rules={[{ required: true, message: 'Please select role' }]}
            >
              <Select placeholder="Select role" options={roleOptions} />
            </Form.Item>
          </Col>

          <Col sm={24} md={12} lg={6} className='w-full'>
            <Form.Item label="Status" name="status">
              <Select
                placeholder="status"
                options={[
                  { label: 'Active', value: 1 },
                  { label: 'Inactive', value: 0 },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <div className="flex justify-content-end gap-1 mt-3">
        <Button size="large" type="default" onClick={() => navigate(getRoutes().users.route())}>
          Cancel
        </Button>
        <Button size="large" type="primary" onClick={() => form.submit()}>
          {userId ? 'Update' : 'Save'}
        </Button>
      </div>

      <Activity
        mode={
          userId && (userId && [ROLES.TEAM_LEAD, ROLES.CUSTOMER].includes(type)) ? 'visible' : 'hidden'
        }
      >
        <Divider className="my-2" />

        <Row gutter={[8, 8]}>
          <Activity mode={type === ROLES.TEAM_LEAD ? 'visible' : 'hidden'}>
            <Col sm={24} lg={24}>
              <Typography.Title level={5}>Assigned Users</Typography.Title>
              <Table
                size="small"
                columns={userColumns}
                dataSource={users}
                pagination={false}
                rowKey={(record) => record?.id}
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: "No users assigned — add users to get started"
                }}
                footer={() => {
                  const availableUsers = _.differenceBy(usersList, users, 'id');
                  return (
                    <div className="flex align-items-center gap-2">
                      <Select
                        size="small"
                        className="w-full max-w-25rem"
                        allowClear
                        showSearch
                        placeholder="Search user..."
                        optionLabelProp="label"
                        value={selectedUser}
                        onChange={(val) => setSelectedUser(val)}
                        filterOption={(input, option) => {
                          const user_name = option['data-name']?.toLowerCase() || '';
                          const email = option['data-email']?.toLowerCase() || '';
                          return (
                            user_name.includes(input.toLowerCase()) ||
                            email.includes(input.toLowerCase())
                          );
                        }}
                        options={availableUsers?.map((usr) => ({
                          label: (
                            <div className="flex flex-column user-select">
                              <Typography.Text strong>
                                {usr?.user_name} {usr?.lead_name && `${usr?.lead_name}`}
                              </Typography.Text>
                              <Typography.Text type="secondary" className="email">
                                {usr.email}
                              </Typography.Text>
                            </div>
                          ),
                          value: usr.id,
                          'data-name': `${usr.first_name} ${usr.last_name}`,
                          'data-email': usr.email,
                        }))}
                      />
                      <Button
                        icon={<PlusOutlined />}
                        variant="solid"
                        color="primary"
                        disabled={!selectedUser}
                        onClick={handleAddUser}
                        size="small"
                      >
                        Add
                      </Button>
                    </div>
                  );
                }}
              />
            </Col>
          </Activity>

          <Activity mode={[ROLES.CUSTOMER].includes(type) ? 'visible' : 'hidden'}>
            <Col sm={24} lg={24}>
              <Typography.Title level={5}>Assigned Customers</Typography.Title>
              <Table
                size="small"
                columns={customerColumns}
                dataSource={customers}
                pagination={false}
                rowKey={(record) => record?.id}
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: "No customers assigned — add customers to get started"
                }}
                footer={() => {
                  const availableCustomers = _.differenceBy(
                    allCustomers,
                    customers,
                    'customer_netsuite_id'
                  );

                  return (
                    <div className="flex align-items-center gap-2">
                      <div className="w-full max-w-25rem">
                        <CustomerSearch
                          placeholder="Search customer..."
                          size="small"
                          value={selectedCustomer}
                          onChange={setSelectedCustomer}
                          data={availableCustomers}
                        />
                      </div>
                      <Button
                        icon={<PlusOutlined />}
                        variant="solid"
                        color="primary"
                        disabled={!selectedCustomer}
                        onClick={handleAddCustomer}
                        size="small"
                      >
                        Add
                      </Button>
                    </div>
                  );
                }}
              />
            </Col>
          </Activity>
        </Row>
      </Activity>
    </div>
  );
};

export default AddUser;
